/* eslint-disable no-underscore-dangle */
import { noop } from 'lodash';
import classnames from 'classnames';
import React, { Component } from 'react';
import { bool, func } from 'prop-types';
import {
  Button,
  CircularProgress,
  TextField,
  MenuItem,
  LinearProgress,
  Typography,
} from '@material-ui/core';

import AudioIndicator from 'Elements/Audio/AudioIndicator';
import TestAudioOutput from 'Elements/Audio/TestAudioOutput';
import Loader from 'Elements/Loader';
import { MeetingContextDeviceControlsProps, withMeetingContext } from 'Scenes/_root/meetingContext';
import withDeviceOrientation from 'Services/withDeviceOrientation';
import styles from './styles.scss';

export class DeviceSettings extends Component {
  // lifecycle tracker to prevent stage changes
  // when some components are unmounted
  _isMounted = false;

  constructor(props) {
    super(props);
    const {
      deviceControls,
      registerMeetingCallbacks,
    } = props;
    const {
      audioIn: audioInDeviceId,
      video: videoDeviceId,
    } = deviceControls.getSelectedDeviceIds();
    const {
      audioIn: audioInDevices,
      video: videoDevices,
      audioOut: audioOutDevices,
    } = deviceControls.getAvailableDevices();
    this.state = {
      audioInIsSaving: false,
      videoIsSaving: false,
      audioOutIsSaving: false,
      showVideoPreview: !!videoDeviceId,
      showAudioPreview: !!audioInDeviceId,
      audioInDevices,
      videoDevices,
      audioOutDevices,
      isGettingAvailableDevices: true,
    };

    this.removeVideoPreview = noop;
    // the video preview element to populate with the video preview
    this.videoPreviewRef = React.createRef();
    this.deregisterMeetingCallbacks = registerMeetingCallbacks('deviceSettings', {
      onSelectedAudioInDeviceIdUpdated: this.onSelectedAudioInDeviceIdUpdated,
      onSelectedVideoDeviceIdUpdated: this.onSelectedVideoDeviceIdUpdated,
      onSelectedAudioOutDeviceIdUpdated: this.onSelectedAudioOutDeviceIdUpdated,
      onPromptForAvailableDevicesEnd: this.onGetAvailableDevicesComplete,
    });
  }

  componentDidMount() {
    const { deviceControls } = this.props;
    deviceControls.promptForAvailableDevices();
  }

  componentWillUnmount() {
    this.removeVideoPreview();
    this.deregisterMeetingCallbacks();
  }

  onGetAvailableDevicesComplete = (availableDevices) => {
    const {
      audioIn: audioInDevices,
      video: videoDevices,
      audioOut: audioOutDevices,
    } = availableDevices;

    this.setState({
      audioInDevices,
      videoDevices,
      audioOutDevices,
      isGettingAvailableDevices: false,
    }, () => {
      this.beginVideoPreview();
    });
  }

  beginVideoPreview = () => {
    const { deviceControls } = this.props;
    const {
      showVideoPreview,
    } = this.state;

    if (showVideoPreview) {
      deviceControls.videoPreviewStart(this.videoPreviewRef.current)
        .then((removeVideoPreview) => {
          this.removeVideoPreview = removeVideoPreview;
        });
    }
  }

  handleClose = () => {
    const {
      onClose,
    } = this.props;
    onClose();
  }

  onSelectedAudioInDeviceIdUpdated = (audioInDeviceId) => {
    this.setState({
      audioInIsSaving: false,
      showAudioPreview: !!audioInDeviceId,
    });
  }

  onSelectedVideoDeviceIdUpdated = (videoDeviceId) => {
    this.setState({
      videoIsSaving: false,
      showVideoPreview: !!videoDeviceId,
    }, () => {
      this.beginVideoPreview();
    });
  }

  onSelectedAudioOutDeviceIdUpdated = () => {
    this.setState({
      audioOutIsSaving: false,
    });
  }

  // when a dropdown selection is made
  handleSelect = (event, id) => {
    const {
      deviceControls,
    } = this.props;
    switch (id) {
      case 'audioIn':
        this.setState({
          audioInIsSaving: true,
        }, () => {
          deviceControls.setSelectedAudioInDeviceId(event.target.value);
        });
        break;
      case 'video':
        this.setState({
          videoIsSaving: true,
        }, () => {
          deviceControls.setSelectedVideoDeviceId(event.target.value);
        });
        break;
      case 'audioOut':
        this.setState({
          audioOutIsSaving: true,
        }, () => {
          deviceControls.setSelectedAudioOutDeviceId(event.target.value);
        });
        break;
      default:
        break;
    }
  };

  // rendering multiple similar fields
  renderField = (field) => {
    const {
      isGettingAvailableDevices,
    } = this.state;
    const isBeingSaved = this.state[`${field.id}IsSaving`];
    return (
      <div className={styles.fieldWrapper}>
        <TextField
          key={field.id}
          disabled={isGettingAvailableDevices || isBeingSaved}
          fullWidth
          id={`${field.id}-select`}
          label={field.label}
          margin='dense'
          onChange={(event) => this.handleSelect(event, field.id)}
          select
          size='small'
          value={field.value}
          variant='outlined'
        >
          {field.devices.map((option, index) => (
            <MenuItem key={index} value={option.deviceId}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
        {(isGettingAvailableDevices || isBeingSaved) && (
          <CircularProgress className={styles.fieldProgress} size={24} />
        )}
      </div>
    );
  }

  render() {
    const {
      deviceControls,
      inModal,
      isAudioOutputSelectionSupported,
      isPortrait,
    } = this.props;
    const {
      audioInDevices,
      videoDevices,
      audioOutDevices,
      showVideoPreview,
      showAudioPreview,
    } = this.state;

    const {
      audioIn: audioInDeviceId,
      video: videoDeviceId,
      audioOut: audioOutDeviceId,
    } = deviceControls.getSelectedDeviceIds();

    // our i/o form fields
    const fields = {
      audioIn: {
        id: 'audioIn',
        label: 'Choose your Microphone',
        value: audioInDeviceId || '',
        devices: audioInDevices,
      },
      audioOut: {
        id: 'audioOut',
        label: 'Choose your Speakers/Headphones',
        value: audioOutDeviceId || '',
        devices: audioOutDevices,
      },
      video: {
        id: 'video',
        label: 'Choose your Camera',
        value: videoDeviceId || '',
        devices: videoDevices,
      },
    };

    return (
      <div className={styles.deviceSettingsWrapper}>
        {inModal && <Typography variant='h5'>Device settings</Typography>}
        <div className={styles.fieldSpacer}>
          {this.renderField(fields.audioIn)}
          {showAudioPreview ? (
            <AudioIndicator />
          ) : (
            <LinearProgress value={0} variant="determinate" />
          )}
        </div>
        {
          isAudioOutputSelectionSupported && (
            <div className={styles.fieldSpacer}>
              {this.renderField(fields.audioOut)}
              <TestAudioOutput />
            </div>
          )
        }
        <div className={styles.fieldSpacer}>
          {this.renderField(fields.video)}
          <div className={classnames(styles.deviceVideoWrapper, isPortrait && styles.deviceVideoPortraitWrapper)}>
            <div className={classnames(styles.videoInner, showVideoPreview && styles.videoEnabledInner)}>
              {showVideoPreview && (
                <div className={styles.videoPreview}>
                  <React.Fragment>
                    <Loader />
                    <video ref={this.videoPreviewRef} className={styles.videoWidth} playsInline />
                  </React.Fragment>
                </div>
              )}
            </div>
          </div>
        </div>
        {inModal && (
          <Button
            color='primary'
            fullWidth
            onClick={this.handleClose}
            type='button'
            variant='contained'
          >
            Done
          </Button>
        )}
      </div>
    );
  }
}

DeviceSettings.propTypes = {
  inModal: bool.isRequired,
  onClose: func.isRequired,
  isPortrait: bool.isRequired,
  deviceControls: MeetingContextDeviceControlsProps,
  registerMeetingCallbacks: func.isRequired,
  isAudioOutputSelectionSupported: bool.isRequired,
};

DeviceSettings.defaultProps = {
  inModal: true,
};

export default withMeetingContext([
  'deviceControls',
  'isAudioOutputSelectionSupported',
  'registerMeetingCallbacks',
])(withDeviceOrientation(DeviceSettings));
